// /money — Money & Usage. Three tabs:
//   • Providers   — stacked area of cost over time, top tenants table
//   • R2 Storage  — per-tenant bytes vs cap, monthly cost estimate
//   • Anomalies   — recent freezes + token spikes + per-key spikes
//
// Time range is URL-driven so a deep-link captures the selected window.
// Default = last 30 days, day granularity.

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useApiClient } from '../../lib/providers.js';
import {
  MONEY_ANOMALIES_QUERY_KEY,
  MONEY_BY_PROVIDER_QUERY_KEY,
  MONEY_STORAGE_QUERY_KEY,
  MONEY_TOP_TENANTS_QUERY_KEY,
  moneyApi,
  type ByProviderBucket,
  type Granularity,
  type StorageRow,
  type TopTenantRow,
} from '../../lib/api/money.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatBytes } from '../../lib/format-bytes.js';

const RANGES = [
  { id: '24h', label: 'Last 24h', ms: 24 * 60 * 60 * 1000, granularity: 'hour' as const },
  { id: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000, granularity: 'day' as const },
  { id: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000, granularity: 'day' as const },
  { id: '90d', label: 'Last 90 days', ms: 90 * 24 * 60 * 60 * 1000, granularity: 'day' as const },
] as const;
type RangeId = (typeof RANGES)[number]['id'];

// Recharts color palette — tuned to read distinctly in both light and
// dark modes. One slot per known provider; unknown providers fall
// through to the last slot (lighter / fallback grey).
const PROVIDER_COLORS: Record<string, string> = {
  openai: 'hsl(168, 76%, 42%)',
  anthropic: 'hsl(28, 88%, 55%)',
  gemini: 'hsl(220, 90%, 60%)',
  grok: 'hsl(280, 70%, 60%)',
  deepseek: 'hsl(340, 80%, 55%)',
  groq: 'hsl(40, 92%, 50%)',
  llama: 'hsl(140, 60%, 50%)',
};
const FALLBACK_COLOR = 'hsl(0, 0%, 60%)';

export function MoneyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeId = (searchParams.get('range') as RangeId | null) ?? '30d';
  // RANGES[2] is the 30d default — `as const` declares the tuple so the
  // compiler narrows the find()-fallback to a non-undefined value.
  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[2];
  const tab = searchParams.get('tab') ?? 'providers';

  const now = useMemo(() => new Date(), []);
  const from = useMemo(() => new Date(now.getTime() - range.ms), [now, range.ms]);

  function setParam(key: string, value: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Money & Usage"
        description="Provider cost, R2 storage usage, and anomaly signals across every tenant."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5 sm:w-48">
          <Label className="text-xs text-muted-foreground">Time range</Label>
          <Select
            value={rangeId}
            onChange={(e) => {
              setParam('range', e.target.value);
            }}
          >
            {RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </div>
        <div className="text-xs text-muted-foreground">
          Granularity: <strong>{range.granularity}</strong>. Longer windows route to Grafana.
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setParam('tab', v);
        }}
      >
        <TabsList>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="storage">R2 Storage</TabsTrigger>
          <TabsTrigger value="anomalies">Anomalies</TabsTrigger>
        </TabsList>
        <TabsContent value="providers" className="mt-6 space-y-6">
          <ProvidersTab from={from} to={now} granularity={range.granularity} />
        </TabsContent>
        <TabsContent value="storage" className="mt-6 space-y-6">
          <StorageTab />
        </TabsContent>
        <TabsContent value="anomalies" className="mt-6 space-y-6">
          <AnomaliesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Providers tab ────────────────────────────────────────────────

function ProvidersTab({ from, to, granularity }: { from: Date; to: Date; granularity: Granularity }) {
  const client = useApiClient();
  const seriesQuery = useQuery({
    queryKey: [...MONEY_BY_PROVIDER_QUERY_KEY, from.toISOString(), to.toISOString(), granularity] as const,
    queryFn: () => moneyApi.byProvider(client, { from, to, granularity }),
  });
  const topQuery = useQuery({
    queryKey: [...MONEY_TOP_TENANTS_QUERY_KEY, from.toISOString(), to.toISOString()] as const,
    queryFn: () => moneyApi.topTenants(client, { from, to, limit: 25 }),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost by provider</CardTitle>
        </CardHeader>
        <CardContent>
          {seriesQuery.isPending ? (
            <Skeleton className="h-72 w-full" />
          ) : seriesQuery.isError ? (
            <ErrorRetry
              title="Failed to load provider series"
              error={seriesQuery.error}
              onRetry={() => {
                void seriesQuery.refetch();
              }}
            />
          ) : seriesQuery.data.buckets.length === 0 ? (
            <EmptyState
              title="No traffic in this window"
              description="Try a wider range or wait for usage."
            />
          ) : (
            <ProviderAreaChart buckets={seriesQuery.data.buckets} granularity={granularity} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top tenants by cost</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {topQuery.isPending ? (
            <div className="space-y-2 px-4 py-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : topQuery.isError ? (
            <div className="px-4">
              <ErrorRetry
                title="Failed to load top tenants"
                error={topQuery.error}
                onRetry={() => {
                  void topQuery.refetch();
                }}
              />
            </div>
          ) : topQuery.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState title="No spend in this window" description="" />
            </div>
          ) : (
            <TopTenantsTable rows={topQuery.data.rows} />
          )}
        </CardContent>
      </Card>
    </>
  );
}

// Pivots the [{ ts, provider, costUsd }] series into [{ ts, openai: ..., anthropic: ... }]
// rows that Recharts can stack. Discovers provider keys at runtime so a
// new provider lights up automatically without a code change.
interface ChartRow {
  tsMs: number;
  // Per-provider cost columns. Indexed by provider name; populated at
  // pivot time. Read by Recharts via dataKey={provider}.
  [provider: string]: number;
}

function ProviderAreaChart({ buckets, granularity }: { buckets: readonly ByProviderBucket[]; granularity: Granularity }) {
  const { data, providers } = useMemo(() => {
    const byTs = new Map<string, ChartRow>();
    const providerSet = new Set<string>();
    for (const b of buckets) {
      const tsKey = b.ts;
      providerSet.add(b.provider);
      const row: ChartRow = byTs.get(tsKey) ?? { tsMs: new Date(b.ts).getTime() };
      row[b.provider] = (row[b.provider] ?? 0) + b.costUsd;
      byTs.set(tsKey, row);
    }
    const rows = Array.from(byTs.values()).sort((a, b) => a.tsMs - b.tsMs);
    return { data: rows, providers: Array.from(providerSet).sort() };
  }, [buckets]);

  return (
    <ResponsiveContainer width="100%" height={320}>
      <AreaChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis
          dataKey="tsMs"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(ms: number) => formatTickMs(ms, granularity)}
          fontSize={11}
        />
        <YAxis
          tickFormatter={(v: number) => `$${v.toFixed(2)}`}
          fontSize={11}
          width={64}
        />
        <Tooltip
          labelFormatter={(ms) => new Date(Number(ms)).toLocaleString()}
          formatter={(value) => formatUsd(Number(value))}
        />
        <Legend />
        {providers.map((p) => (
          <Area
            key={p}
            type="monotone"
            dataKey={p}
            stackId="1"
            name={p}
            stroke={PROVIDER_COLORS[p] ?? FALLBACK_COLOR}
            fill={PROVIDER_COLORS[p] ?? FALLBACK_COLOR}
            fillOpacity={0.4}
            isAnimationActive={false}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function formatTickMs(ms: number, granularity: Granularity): string {
  const d = new Date(ms);
  if (granularity === 'hour') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function TopTenantsTable({ rows }: { rows: readonly TopTenantRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead className="text-right">Cost</TableHead>
          <TableHead className="text-right">Calls</TableHead>
          <TableHead className="text-right">Tokens (in / out)</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.tenantId}>
            <TableCell>
              <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                {r.tenantName}
              </Link>
            </TableCell>
            <TableCell>
              {r.planCode ? <Badge variant="secondary">{r.planCode}</Badge> : '—'}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">{formatUsd(r.costUsd)}</TableCell>
            <TableCell className="text-right font-mono text-sm">
              {r.callCount.toLocaleString()}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {r.inputTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── R2 Storage tab ───────────────────────────────────────────────

function StorageTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: MONEY_STORAGE_QUERY_KEY,
    queryFn: () => moneyApi.storage(client, { limit: 100 }),
  });

  if (query.isPending) return <Skeleton className="h-64 w-full" />;
  if (query.isError) {
    return (
      <ErrorRetry
        title="Failed to load storage"
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const totalBytes = query.data.rows.reduce((acc, r) => acc + r.bytes, 0);
  const totalCost = query.data.rows.reduce((acc, r) => acc + r.estimatedMonthlyCostUsd, 0);
  const overCap = query.data.rows.filter(
    (r) => r.utilizationPct !== null && r.utilizationPct > 1,
  );

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard label="Total stored" value={formatBytes(totalBytes)} />
        <SummaryCard
          label="Estimated monthly R2 cost"
          value={formatUsd(totalCost, { precise: false })}
          hint="$0.015 / GB-month"
        />
        <SummaryCard
          label="Over cap"
          value={`${String(overCap.length)} tenant${overCap.length === 1 ? '' : 's'}`}
          hint={overCap.length === 0 ? 'Healthy' : 'Plan-cap reconciliation pending'}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-tenant storage</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {query.data.rows.length === 0 ? (
            <div className="px-4">
              <EmptyState title="No KB documents yet" description="" />
            </div>
          ) : (
            <StorageTable rows={query.data.rows} />
          )}
        </CardContent>
      </Card>
    </>
  );
}

function StorageTable({ rows }: { rows: readonly StorageRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tenant</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead className="text-right">Bytes</TableHead>
          <TableHead className="text-right">Docs</TableHead>
          <TableHead>Cap</TableHead>
          <TableHead className="text-right">Est. cost / mo</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.tenantId}>
            <TableCell>
              <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                {r.tenantName}
              </Link>
            </TableCell>
            <TableCell>
              {r.planCode ? <Badge variant="secondary">{r.planCode}</Badge> : '—'}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">{formatBytes(r.bytes)}</TableCell>
            <TableCell className="text-right font-mono text-sm">{r.docCount}</TableCell>
            <TableCell>
              <CapCell row={r} />
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {formatUsd(r.estimatedMonthlyCostUsd, { precise: r.estimatedMonthlyCostUsd < 0.01 })}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CapCell({ row }: { row: StorageRow }) {
  if (row.capBytes === null) {
    return <span className="text-xs text-muted-foreground italic">unlimited</span>;
  }
  if (row.utilizationPct === null) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const pct = row.utilizationPct;
  const overCap = pct > 1;
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{formatBytes(row.capBytes)}</span>
        {overCap && <Badge variant="destructive">over cap</Badge>}
      </div>
      <div className="h-1.5 w-32 rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${overCap ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${Math.min(pct * 100, 100).toFixed(0)}%` }}
        />
      </div>
      <span className="text-[10px] text-muted-foreground">{(pct * 100).toFixed(0)}% used</span>
    </div>
  );
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="space-y-1 py-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

// ─── Anomalies tab ────────────────────────────────────────────────

function AnomaliesTab() {
  const client = useApiClient();
  const query = useQuery({
    queryKey: MONEY_ANOMALIES_QUERY_KEY,
    queryFn: () => moneyApi.anomalies(client),
  });

  if (query.isPending) return <Skeleton className="h-64 w-full" />;
  if (query.isError) {
    return (
      <ErrorRetry
        title="Failed to load anomalies"
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const { recentFreezes, tokenSpikes, keySpikes } = query.data;
  const empty = recentFreezes.length === 0 && tokenSpikes.length === 0 && keySpikes.length === 0;

  if (empty) {
    return (
      <EmptyState
        title="No anomalies detected"
        description="The breaker hasn't fired and no token/key spikes are above threshold."
      />
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent breaker freezes (last 7 days)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {recentFreezes.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">None.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Frozen at</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentFreezes.map((r) => (
                  <TableRow key={`${r.tenantId}-${r.frozenAt}`}>
                    <TableCell>
                      <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                        {r.tenantName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.frozenAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{r.frozenReason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Token spikes (last 24h, ≥ 5× p99)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {tokenSpikes.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">None.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Conversation</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">7d p99</TableHead>
                  <TableHead className="text-right">Ratio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokenSpikes.map((r) => (
                  <TableRow key={r.conversationId}>
                    <TableCell>
                      <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                        {r.tenantName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{r.conversationId.slice(0, 8)}…</code>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {r.totalTokens.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {r.baselineP99.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">{r.ratio.toFixed(1)}×</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per-API-key spikes (last hour, ≥ 10× 7d-avg)</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {keySpikes.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">None.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>API key</TableHead>
                  <TableHead className="text-right">Hourly cost</TableHead>
                  <TableHead className="text-right">Baseline</TableHead>
                  <TableHead className="text-right">Ratio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keySpikes.map((r) => (
                  <TableRow key={r.apiKeyId}>
                    <TableCell>
                      <Link to={`/tenants/${r.tenantId}`} className="hover:text-foreground">
                        {r.tenantName}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{r.apiKeyPrefix}…</code>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatUsd(r.hourlyCostUsd)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatUsd(r.baselineHourlyUsd, { precise: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">{r.ratio.toFixed(1)}×</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
